// ─── Plantillas HTML de los emails transaccionales ──────────────────────────
//
// Diseño en línea con la marca mezo: fondo #080706, oro #C8903F, crema
// #F4ECD8, Fraunces italic para el logo.
//
// MODO OSCURO: antes se declaraba `color-scheme: light` creyendo que eso
// evitaba que los clientes forzaran oscuro. No lo evita — la app de Gmail en
// Android invierte igual si el correo no trae estilos oscuros propios, y el
// resultado daba vuelta el diseño (banda clara, cuerpo oscuro). Ahora se
// declara `light dark` y se entrega una versión oscura hecha por nosotros:
// ver `emailDarkMode.js`, que es la ÚNICA fuente de verdad de esos colores.
//
// Si quieres editar el estilo de TODOS los emails, edita `layout()` abajo.
// Si vas a agregar un correo NUEVO con su propio <head>, pégale
// META_MODO_OSCURO y ESTILO_MODO_OSCURO o se verá invertido en el celular.

import { ESTILO_MODO_OSCURO, META_MODO_OSCURO } from './emailDarkMode';

function formatCOP(n) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency', currency: 'COP', maximumFractionDigits: 0,
  }).format(n);
}

// Layout maestro — todos los emails lo usan.
// Sistema de diseño Claude Design (mismo shell que templateBienvenida):
// header banda oscura con wordmark Fraunces italic + eyebrow, body crema
// #F4ECD8, CTA en banda oscura redondeada, firma "el equipo de mezo" y
// footer #EBE2C9 con marca + Instagram. Table-based para Outlook/Apple Mail.
function layout({ titulo, cuerpo, cta, ctaHref, preheader = '' }) {
  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="x-apple-disable-message-reformatting" />
  ${META_MODO_OSCURO}
  <title>${titulo}</title>
  <!--[if mso]><style>* { font-family: Georgia, serif !important; }</style><![endif]-->
  <link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;1,9..144,500;1,9..144,600&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />
  ${ESTILO_MODO_OSCURO}
</head>
<body style="margin:0;padding:0;background:#E8E4DB;font-family:'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#2B2420;-webkit-font-smoothing:antialiased;">
${preheader ? `<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:#E8E4DB;">${preheader}</div>` : ''}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#E8E4DB;padding:24px 12px;">
  <tr>
    <td align="center">
      <table role="presentation" width="560" cellpadding="0" cellspacing="0" border="0" style="background:#F4ECD8;border-radius:14px;overflow:hidden;max-width:560px;width:100%;">

        <!-- Header banda oscura -->
        <tr><td style="background:#080706;padding:22px 36px;border-bottom:1px solid #2A2520;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0">
            <tr>
              <td><span style="font-family:'Fraunces',Georgia,serif;font-style:italic;font-weight:600;font-size:26px;color:#C8903F;letter-spacing:-0.045em;">mezo</span></td>
              <td align="right"><span style="font-size:10px;letter-spacing:0.24em;text-transform:uppercase;color:#B8A88E;">Punto de venta · Colombia</span></td>
            </tr>
          </table>
        </td></tr>

        <!-- Contenido -->
        <tr><td style="padding:36px 44px 8px;background:#F4ECD8;font-size:15px;line-height:1.6;color:#3A322A;">
          ${cuerpo}
        </td></tr>

        ${cta && ctaHref ? `
        <!-- CTA -->
        <tr><td style="padding:14px 44px 8px;background:#F4ECD8;">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0">
            <tr><td style="background:#080706;border-radius:10px;">
              <a href="${ctaHref}" target="_blank" style="display:inline-block;padding:14px 30px;color:#F4ECD8;text-decoration:none;font-family:'DM Sans',sans-serif;font-weight:600;font-size:15px;border:1px solid #080706;border-radius:10px;letter-spacing:-0.005em;">${cta} →</a>
            </td></tr>
          </table>
        </td></tr>` : ''}

        <!-- Firma -->
        <tr><td style="padding:22px 44px 28px;background:#F4ECD8;">
          <div style="font-size:14px;color:#3A322A;line-height:1.55;margin-bottom:4px;">Con cariño,</div>
          <div style="font-family:'Fraunces',Georgia,serif;font-style:italic;font-size:18px;color:#C8903F;letter-spacing:-0.01em;">el equipo de mezo</div>
          <div style="font-size:12px;color:#7A6A58;margin-top:4px;letter-spacing:0.04em;">TemeT · Bogotá, Colombia</div>
        </td></tr>

        <!-- Footer -->
        <tr><td style="background:#EBE2C9;padding:18px 44px 22px;border-top:1px solid #E8DDC2;font-size:11px;color:#7A6A58;line-height:1.6;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:8px;">
            <tr>
              <td><span style="font-family:'Fraunces',serif;font-style:italic;font-size:13px;color:#2B2420;">mezo</span></td>
              <td align="right" style="font-size:11px;color:#7A6A58;">mezopos.com · <a href="https://www.instagram.com/mezo.pos/" style="color:#2B2420;text-decoration:underline;">@mezo.pos</a></td>
            </tr>
          </table>
          <div style="font-size:10.5px;color:#7A6A58;opacity:0.85;">
            ¿Dudas? Escríbenos al WhatsApp +57 305 426 5316 o a
            <a href="mailto:soporte@mezopos.com" style="color:#7A6A58;">soporte@mezopos.com</a>
          </div>
        </td></tr>

      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
}

// Helper de estilo para los <h1> de los cuerpos — Fraunces con acento italic.
// Uso: tituloEmail('Pago', 'confirmado') → "Pago <i>confirmado</i>" en dorado.
function tituloEmail(normal, acento) {
  return `<h1 style="font-family:'Fraunces',Georgia,serif;font-weight:600;font-size:28px;line-height:1.15;letter-spacing:-0.02em;color:#2B2420;margin:0 0 18px;">
    ${normal} <span style="font-style:italic;color:#C8903F;">${acento}</span>
  </h1>`;
}

// ─── Template 1: Bienvenida al registrarse ──────────────────────────────────
// Diseño de Claude Design (handoff "design_handoff_bienvenida/correo/00-welcome.html").
// Layout table-based para compatibilidad con clientes de correo viejos
// (Outlook, Apple Mail, Yahoo). Fuente Fraunces + DM Sans con fallback a
// Georgia / system.
//
// Variables interpoladas:
//   - ${negocioNombre} → nombre del negocio del cliente
//   - https://mezopos.com/guia es la ruta de la guía web
//     (alternativa al PDF adjunto cuando aún no lo hemos implementado).
export function templateBienvenida({ negocioNombre }) {
  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<meta name="x-apple-disable-message-reformatting" />
${META_MODO_OSCURO}
<title>¡Bienvenido a mezo, ${negocioNombre}!</title>
<!--[if mso]><style>* { font-family: Georgia, serif !important; }</style><![endif]-->
<link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,500;1,9..144,600&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />
${ESTILO_MODO_OSCURO}
</head>
<body style="margin:0;padding:0;background:#E8E4DB;font-family:'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#2B2420;-webkit-font-smoothing:antialiased;">
<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:#E8E4DB;">Veci, qué bueno tenerte en mezo. Tu manual de bolsillo te espera adentro para empezar a cobrar hoy mismo.</div>

<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#E8E4DB;padding:24px 12px;">
  <tr>
    <td align="center">
      <table role="presentation" width="560" cellpadding="0" cellspacing="0" border="0" style="background:#F4ECD8;border-radius:14px;overflow:hidden;max-width:560px;width:100%;">

        <!-- Header -->
        <tr><td style="background:#080706;padding:22px 36px;border-bottom:1px solid #2A2520;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0">
            <tr>
              <td><span style="font-family:'Fraunces',Georgia,serif;font-style:italic;font-weight:600;font-size:26px;color:#C8903F;letter-spacing:-0.045em;">mezo</span></td>
              <td align="right"><span style="font-size:10px;letter-spacing:0.24em;text-transform:uppercase;color:#B8A88E;">Punto de venta · Colombia</span></td>
            </tr>
          </table>
        </td></tr>

        <!-- Hero -->
        <tr><td style="padding:40px 44px 8px;background:#F4ECD8;">
          <div style="font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#B8802F;font-weight:600;margin-bottom:12px;">Bienvenida a la familia</div>
          <h1 style="font-family:'Fraunces',Georgia,serif;font-weight:600;font-size:32px;line-height:1.12;letter-spacing:-0.02em;color:#2B2420;margin:0;">
            Veci, ¡qué bueno <span style="font-style:italic;color:#C8903F;">tenerte</span> en mezo!
          </h1>
        </td></tr>

        <!-- Body copy -->
        <tr><td style="padding:18px 44px 8px;background:#F4ECD8;font-size:15px;line-height:1.6;color:#3A322A;">
          <p style="margin:0 0 16px;">Gracias por confiar en nosotros con <b style="color:#2B2420;">${negocioNombre}</b>. Desde hoy tienes el POS más fácil, justo y colombiano de tu lado — y vamos a acompañarte en cada paso.</p>
          <p style="margin:0 0 16px;">Para que arranques con el pie derecho, te dejamos tu <b style="color:#2B2420;">Guía de Bienvenida</b> 📖 — un manual de bolsillo con todo lo que necesitas: cómo cobrar tu primera venta, manejar mesas, activar tu menú QR, y más.</p>
        </td></tr>

        <!-- CTA -->
        <tr><td style="padding:8px 44px 4px;background:#F4ECD8;">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0">
            <tr><td style="background:#080706;border-radius:10px;">
              <a href="https://mezopos.com/dashboard" target="_blank" style="display:inline-block;padding:14px 30px;color:#F4ECD8;text-decoration:none;font-family:'DM Sans',sans-serif;font-weight:600;font-size:15px;border:1px solid #080706;border-radius:10px;letter-spacing:-0.005em;">Entrar a mezo →</a>
            </td></tr>
          </table>
        </td></tr>

        <!-- Guide attachment callout -->
        <tr><td style="padding:20px 44px 4px;background:#F4ECD8;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#080706;border-radius:12px;">
            <tr>
              <td style="padding:18px 20px;" valign="middle">
                <table width="100%" cellpadding="0" cellspacing="0" border="0">
                  <tr>
                    <td valign="middle" width="46" style="padding-right:14px;">
                      <table cellpadding="0" cellspacing="0" border="0"><tr><td align="center" valign="middle" style="width:46px;height:46px;background:rgba(200,144,63,0.14);border:1px solid rgba(200,144,63,0.3);border-radius:10px;font-size:20px;color:#C8903F;">📖</td></tr></table>
                    </td>
                    <td valign="middle">
                      <a href="https://mezopos.com/guia" target="_blank" style="text-decoration:none;color:inherit;">
                        <div style="font-family:'Fraunces',Georgia,serif;font-weight:600;font-style:italic;font-size:16px;color:#F4ECD8;line-height:1.2;">Guía de mezo — todo, paso a paso</div>
                        <div style="font-size:12px;color:#B8A88E;margin-top:3px;">cada pantalla con capturas y dónde hacer clic · abrir →</div>
                      </a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td></tr>

        <!-- Contrato attachment callout -->
        <tr><td style="padding:10px 44px 4px;background:#F4ECD8;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#080706;border-radius:12px;">
            <tr>
              <td style="padding:18px 20px;" valign="middle">
                <table width="100%" cellpadding="0" cellspacing="0" border="0">
                  <tr>
                    <td valign="middle" width="46" style="padding-right:14px;">
                      <table cellpadding="0" cellspacing="0" border="0"><tr><td align="center" valign="middle" style="width:46px;height:46px;background:rgba(200,144,63,0.14);border:1px solid rgba(200,144,63,0.3);border-radius:10px;font-size:20px;color:#C8903F;">📄</td></tr></table>
                    </td>
                    <td valign="middle">
                      <a href="https://mezopos.com/contrato/" target="_blank" style="text-decoration:none;color:inherit;">
                        <div style="font-family:'Fraunces',Georgia,serif;font-weight:600;font-style:italic;font-size:16px;color:#F4ECD8;line-height:1.2;">Contrato de servicio</div>
                        <div style="font-size:12px;color:#B8A88E;margin-top:3px;">los términos claros, sin letra menuda · click para abrir →</div>
                      </a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td></tr>

        <!-- Quick first steps -->
        <tr><td style="padding:22px 44px 6px;background:#F4ECD8;">
          <div style="font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#7A6A58;margin-bottom:14px;">Tus primeros pasos</div>
          <table width="100%" cellpadding="0" cellspacing="0" border="0">
            <tr>
              <td valign="top" width="32" style="padding:0 12px 14px 0;"><span style="display:inline-block;width:24px;height:24px;border:1.5px solid #C8903F;border-radius:50%;color:#C8903F;font-family:'Fraunces',serif;font-weight:600;font-size:12px;text-align:center;line-height:23px;">1</span></td>
              <td valign="middle" style="padding-bottom:14px;font-size:14px;color:#3A322A;">Crea tu primer producto en <b>Productos</b>.</td>
            </tr>
            <tr>
              <td valign="top" width="32" style="padding:0 12px 14px 0;"><span style="display:inline-block;width:24px;height:24px;border:1.5px solid #C8903F;border-radius:50%;color:#C8903F;font-family:'Fraunces',serif;font-weight:600;font-size:12px;text-align:center;line-height:23px;">2</span></td>
              <td valign="middle" style="padding-bottom:14px;font-size:14px;color:#3A322A;">Activa tu menú QR y descárgalo para tus mesas.</td>
            </tr>
            <tr>
              <td valign="top" width="32" style="padding:0 12px 0 0;"><span style="display:inline-block;width:24px;height:24px;border:1.5px solid #C8903F;border-radius:50%;color:#C8903F;font-family:'Fraunces',serif;font-weight:600;font-size:12px;text-align:center;line-height:23px;">3</span></td>
              <td valign="middle" style="font-size:14px;color:#3A322A;">Haz tu primera venta de prueba en <b>POS / Caja</b>.</td>
            </tr>
          </table>
        </td></tr>

        <!-- Help line -->
        <tr><td style="padding:22px 44px 8px;background:#F4ECD8;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:rgba(61,170,104,0.08);border:1px solid rgba(61,170,104,0.25);border-radius:10px;">
            <tr><td style="padding:14px 16px;font-size:13px;color:#2E8450;line-height:1.5;">
              <b>¿Te trabas en algo?</b> Escríbenos al WhatsApp <b>+57 305 426 5316</b> o entra a mezopos.com/ayuda. Vamos a estar ahí.
            </td></tr>
          </table>
        </td></tr>

        <!-- Signature -->
        <tr><td style="padding:18px 44px 28px;background:#F4ECD8;">
          <div style="font-size:14px;color:#3A322A;line-height:1.55;margin-bottom:4px;">Con cariño,</div>
          <div style="font-family:'Fraunces',Georgia,serif;font-style:italic;font-size:18px;color:#C8903F;letter-spacing:-0.01em;">el equipo de mezo</div>
          <div style="font-size:12px;color:#7A6A58;margin-top:4px;letter-spacing:0.04em;">TemeT · Bogotá, Colombia</div>
        </td></tr>

        <!-- Footer -->
        <tr><td style="background:#EBE2C9;padding:18px 44px 22px;border-top:1px solid #E8DDC2;font-size:11px;color:#7A6A58;line-height:1.6;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:8px;">
            <tr>
              <td><span style="font-family:'Fraunces',serif;font-style:italic;font-size:13px;color:#2B2420;">mezo</span></td>
              <td align="right" style="font-size:11px;color:#7A6A58;">mezopos.com · <a href="https://www.instagram.com/mezo.pos/" style="color:#2B2420;text-decoration:underline;">@mezo.pos</a></td>
            </tr>
          </table>
          <div style="font-size:10.5px;color:#7A6A58;opacity:0.85;">
            TemeT · Bogotá, Colombia<br />
            Te llegó este correo porque acabas de crear tu cuenta en mezo. Si no fuiste tú, ignóralo o escríbenos.
          </div>
        </td></tr>

      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
}

// ─── Template 2: Factura tras pago confirmado ───────────────────────────────
export function templateFacturaPagada({ negocioNombre, plan, monto, fecha, periodo }) {
  const planUpper = (plan ?? '').toUpperCase();
  const fechaStr = fecha
    ? new Date(fecha).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('es-CO');
  const cuerpo = `
    <div style="font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#B8802F;font-weight:600;margin-bottom:12px;">Recibo de pago</div>
    ${tituloEmail('Pago', 'confirmado ✓')}
    <p style="margin:0 0 16px;">
      Hola <b style="color:#2B2420;">${negocioNombre}</b>, tu pago en Wompi se procesó correctamente.
      Tu plan <b style="color:#2B2420;">${planUpper}</b> ya está activo.
    </p>
    <!-- Recibo en banda oscura — protagonista del email -->
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#080706;border-radius:12px;margin:20px 0;">
      <tr><td style="padding:20px 22px;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
          <tr><td style="color:#7A6A58; font-size:12px; padding:4px 0;">Plan</td>
              <td align="right" style="color:#F4ECD8; font-size:13px; font-weight:600; padding:4px 0;">${planUpper}</td></tr>
          <tr><td style="color:#7A6A58; font-size:12px; padding:4px 0;">Período</td>
              <td align="right" style="color:#F4ECD8; font-size:13px; font-weight:600; padding:4px 0;">${periodo === 'anual' ? 'Anual' : 'Mensual'}</td></tr>
          <tr><td style="color:#7A6A58; font-size:12px; padding:4px 0;">Fecha</td>
              <td align="right" style="color:#F4ECD8; font-size:13px; font-weight:600; padding:4px 0;">${fechaStr}</td></tr>
          <tr><td style="color:#E4B878; font-size:12px; letter-spacing:0.1em; text-transform:uppercase; padding:14px 0 4px; border-top:1px solid #2A2520;">Total pagado</td>
              <td align="right" style="font-family:'Fraunces',Georgia,serif; color:#C8903F; font-size:24px; font-weight:600; padding:14px 0 4px; border-top:1px solid #2A2520;">${formatCOP(monto)}</td></tr>
        </table>
      </td></tr>
    </table>
    <p style="margin:0; font-size:14px;">
      Te enviaremos tu próxima factura unos días antes del vencimiento. Puedes cancelar
      cuando quieras desde Configuración → Plan y suscripción.
    </p>
  `;
  return layout({
    titulo:  'Factura mezo',
    preheader: `Pago confirmado · Plan ${planUpper} activo · ${formatCOP(monto)}`,
    cuerpo,
    cta:     'Ver mi cuenta',
    ctaHref: 'https://mezopos.com/configuracion',
  });
}

// ─── Template 3: Invitación a empleado ──────────────────────────────────────
export function templateInvitacionEmpleado({ empleadoNombre, negocioNombre, rol }) {
  const cuerpo = `
    <div style="font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#B8802F;font-weight:600;margin-bottom:12px;">Invitación al equipo</div>
    ${tituloEmail(`Hola ${empleadoNombre},`, 'te invitaron 👋')}
    <p style="margin:0 0 16px;">
      <b style="color:#2B2420;">${negocioNombre}</b> te invitó a unirte a su equipo en mezo
      como <b style="color:#2B2420;">${rol}</b>.
    </p>
    <p style="margin:0 0 16px;">
      mezo es el POS que usa el negocio para tomar pedidos, manejar mesas y cobrar.
      Cuando aceptes vas a poder loguearte con tu correo y empezar a trabajar de una.
    </p>
    <!-- Badge del rol en banda oscura -->
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:4px 0 8px;">
      <tr><td style="background:rgba(200,144,63,0.12);border:1px solid rgba(200,144,63,0.35);border-radius:8px;padding:8px 16px;">
        <span style="font-size:13px;color:#B8802F;font-weight:600;">Tu rol: ${rol}</span>
      </td></tr>
    </table>
  `;
  return layout({
    titulo:  `Te invitaron a ${negocioNombre} en mezo`,
    preheader: `${negocioNombre} te invitó como ${rol} — crea tu cuenta y empieza hoy.`,
    cuerpo,
    cta:     'Crear mi cuenta',
    ctaHref: 'https://mezopos.com/register',
  });
}

// ─── Template 4: Plan / demo activado ───────────────────────────────────────
export function templatePlanActivado({ negocioNombre, plan, expiraEn }) {
  const planUpper = (plan ?? '').toUpperCase();
  const expiraStr = expiraEn
    ? new Date(expiraEn).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })
    : null;
  const cuerpo = `
    <div style="font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#B8802F;font-weight:600;margin-bottom:12px;">Plan activado</div>
    ${tituloEmail(`Tu plan ${planUpper}`, 'está activo ✨')}
    <p style="margin:0 0 16px;">
      Hola <b style="color:#2B2420;">${negocioNombre}</b>, ya puedes disfrutar de todos los
      beneficios de tu plan ${planUpper}.
    </p>
    ${expiraStr ? `
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:rgba(217,164,55,0.10);border:1px solid rgba(217,164,55,0.30);border-radius:10px;margin:0 0 16px;">
        <tr><td style="padding:12px 16px;font-size:13px;color:#8A6D2F;line-height:1.5;">
          ⏳ Tu acceso expira el <b>${expiraStr}</b>. Antes de esa fecha, escoge un plan
          para mantener todos los features sin interrupciones.
        </td></tr>
      </table>
    ` : ''}
    <p style="margin:0; font-size:14px;">
      Tip: empieza por configurar tu factura en Configuración → Tributaria. Te toma 2 minutos
      y ya quedas con el NIT y la resolución DIAN listas para imprimir tickets.
    </p>
  `;
  return layout({
    titulo:  `Tu plan ${planUpper} está activo`,
    preheader: `Tu plan ${planUpper} ya está activo en mezo. Entra y aprovéchalo.`,
    cuerpo,
    cta:     'Abrir mezo',
    ctaHref: 'https://mezopos.com/dashboard',
  });
}

// ─── Template 5: Recompensa por referido (1 mes Pro gratis) ─────────────────
export function templateReferidoGanado({ negocioNombre, referidoNombre }) {
  const cuerpo = `
    <div style="font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#B8802F;font-weight:600;margin-bottom:12px;">Recompensa de referido</div>
    ${tituloEmail('Ganaste un', 'mes gratis 🎉')}
    <p style="margin:0 0 16px;">
      ¡Gracias por recomendar mezo! <b style="color:#2B2420;">${referidoNombre ?? 'Un negocio'}</b> se
      unió usando tu código de referido, así que le sumamos <b>1 mes de Pro gratis</b> a tu cuenta
      <b style="color:#2B2420;">${negocioNombre ?? 'tu negocio'}</b>.
    </p>
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:rgba(61,170,104,0.10);border:1px solid rgba(61,170,104,0.30);border-radius:10px;margin:0 0 16px;">
      <tr><td style="padding:12px 16px;font-size:13px;color:#2E8450;line-height:1.5;">
        🎁 Sigue invitando: por <b>cada negocio</b> que traigas con tu código, te regalamos otro mes de Pro.
      </td></tr>
    </table>
  `;
  return layout({
    titulo:  'Ganaste un mes gratis de Pro',
    preheader: `${referidoNombre ?? 'Un negocio'} se unió con tu código — te regalamos un mes de Pro.`,
    cuerpo,
    cta:     'Ver mi cuenta',
    ctaHref: 'https://mezopos.com/planes',
  });
}

// ─── Template 6: Invita y gana (outbound — promociona el programa) ──────────
// Diseño de Claude Design (handoff #7). `mensajeWhatsapp` debe venir URL-encoded.
export function templateInvitaYGana({ codigo, link, mensajeWhatsapp }) {
  return `<!DOCTYPE html>
<html lang="es"><head>
<meta charset="UTF-8" /><meta name="viewport" content="width=device-width, initial-scale=1" />
<meta name="x-apple-disable-message-reformatting" />
${META_MODO_OSCURO}
<!--[if mso]><style>* { font-family: Georgia, serif !important; }</style><![endif]-->
${ESTILO_MODO_OSCURO}
</head>
<body style="margin:0;padding:0;background:#E8E4DB;font-family:'DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#2B2420;-webkit-font-smoothing:antialiased;">
<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:#E8E4DB;">Comparte tu código mezo. Cada negocio que se una = un mes gratis de Pro.</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#E8E4DB;padding:24px 12px;">
  <tr><td align="center">
    <table role="presentation" width="560" cellpadding="0" cellspacing="0" border="0" style="background:#F4ECD8;border-radius:14px;overflow:hidden;max-width:560px;width:100%;">
      <tr><td style="background:#080706;padding:22px 36px;border-bottom:1px solid #2A2520;">
        <table width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
          <td><span style="font-family:'Fraunces',Georgia,serif;font-style:italic;font-weight:600;font-size:26px;color:#C8903F;letter-spacing:-0.045em;">mezo</span></td>
          <td align="right"><span style="font-size:10px;letter-spacing:0.24em;text-transform:uppercase;color:#B8A88E;">Punto de venta · Colombia</span></td>
        </tr></table>
      </td></tr>
      <tr><td style="padding:38px 44px 8px;background:#F4ECD8;">
        <div style="font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#B8802F;font-weight:600;margin-bottom:12px;">Invita y gana</div>
        <h1 style="font-family:'Fraunces',Georgia,serif;font-weight:600;font-size:31px;line-height:1.14;letter-spacing:-0.02em;color:#2B2420;margin:0;">Trae a otro veci y <span style="font-style:italic;color:#C8903F;">gánate</span> un mes de Pro.</h1>
      </td></tr>
      <tr><td style="padding:18px 44px 0;background:#F4ECD8;font-size:15px;line-height:1.6;color:#3A322A;"><p style="margin:0;">Así de simple: comparte tu código o tu link. Cada negocio que se registre en mezo con él te suma <b style="color:#2B2420;">un mes completo de Pro, gratis</b>. Sin límite — entre más invites, más meses.</p></td></tr>
      <tr><td style="padding:24px 44px 0;background:#F4ECD8;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#080706;border-radius:14px;border:1px solid #2A2520;">
          <tr><td style="height:3px;background:#C8903F;line-height:3px;font-size:0;border-radius:14px 14px 0 0;">&nbsp;</td></tr>
          <tr><td style="padding:24px 28px;text-align:center;">
            <div style="font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:#B8A88E;margin-bottom:12px;font-weight:600;">Tu código para compartir</div>
            <div style="font-family:'JetBrains Mono',monospace;font-weight:500;font-size:34px;letter-spacing:0.22em;color:#C8903F;line-height:1;">${codigo}</div>
            <div style="font-size:12px;color:#B8A88E;margin-top:14px;word-break:break-all;">${link}</div>
          </td></tr>
        </table>
      </td></tr>
      <tr><td style="padding:26px 44px 6px;background:#F4ECD8;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
          <td style="background:#C8903F;border-radius:10px;">
            <a href="https://wa.me/?text=${mensajeWhatsapp}" target="_blank" style="display:inline-block;padding:15px 32px;color:#080706;text-decoration:none;font-family:'DM Sans',sans-serif;font-weight:600;font-size:15px;border-radius:10px;letter-spacing:-0.005em;">Compartir por WhatsApp →</a>
          </td>
        </tr></table>
      </td></tr>
      <tr><td style="padding:28px 44px 0;background:#F4ECD8;"><div style="font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#7A6A58;margin-bottom:16px;">Cómo funciona</div>
        <table width="100%" cellpadding="0" cellspacing="0" border="0">
          <tr><td valign="top" width="40" style="padding:0 12px 14px 0;"><table cellpadding="0" cellspacing="0" border="0"><tr><td align="center" valign="middle" style="width:26px;height:26px;border:1.5px solid #C8903F;border-radius:50%;color:#C8903F;font-family:'Fraunces',serif;font-weight:600;font-size:13px;line-height:26px;">1</td></tr></table></td><td valign="middle" style="padding-bottom:14px;font-size:14.5px;color:#3A322A;line-height:1.4;">Comparte tu código o link con otro negocio.</td></tr>
          <tr><td valign="top" width="40" style="padding:0 12px 14px 0;"><table cellpadding="0" cellspacing="0" border="0"><tr><td align="center" valign="middle" style="width:26px;height:26px;border:1.5px solid #C8903F;border-radius:50%;color:#C8903F;font-family:'Fraunces',serif;font-weight:600;font-size:13px;line-height:26px;">2</td></tr></table></td><td valign="middle" style="padding-bottom:14px;font-size:14.5px;color:#3A322A;line-height:1.4;">Ellos se registran y activan su plan en mezo.</td></tr>
          <tr><td valign="top" width="40" style="padding:0 12px 0 0;"><table cellpadding="0" cellspacing="0" border="0"><tr><td align="center" valign="middle" style="width:26px;height:26px;border:1.5px solid #3DAA68;border-radius:50%;color:#3DAA68;font-family:'Fraunces',serif;font-weight:600;font-size:13px;line-height:26px;">3</td></tr></table></td><td valign="middle" style="font-size:14.5px;color:#3A322A;line-height:1.4;"><b style="color:#2B2420;">Te cae un mes gratis de Pro</b>, automático.</td></tr>
        </table>
      </td></tr>
      <tr><td style="padding:20px 44px 28px;background:#F4ECD8;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#EDF7F1;border:1px solid rgba(61,170,104,0.25);border-radius:10px;">
          <tr><td style="padding:14px 16px;font-size:13px;color:#2E8450;line-height:1.55;">Tus referidos también arrancan con sus 30 días gratis — todos ganan. El mes se abona a tu próxima renovación.</td></tr>
        </table>
      </td></tr>
      <tr><td style="background:#EBE2C9;padding:18px 44px 22px;border-top:1px solid #E8DDC2;font-size:11px;color:#7A6A58;line-height:1.6;">
        <span style="font-family:'Fraunces',serif;font-style:italic;font-size:13px;color:#2B2420;">mezo</span> · TemeT · Bogotá · WhatsApp +57 305 426 5316 · mezopos.com
      </td></tr>
    </table>
  </td></tr>
</table>
</body></html>`;
}
